import React, { useState, useEffect } from "react";
import { authService } from "../../services/auth.service";
import GoogleIcon from "../../assets/icons/GoogleIcon";
import AppleIcon from "../../assets/icons/AppleIcon";
import { auth } from "../../config/firebase";
import {
  GoogleAuthProvider,
  linkWithPopup,
  signOut,
  updateProfile,
} from "firebase/auth";
import "./AuthPage.css";

const LoginPage = ({ onToggleView }) => {
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [globalError, setGlobalError] = useState(null);
  const [globalSuccess, setGlobalSuccess] = useState(null);
  const [loading, setLoading] = useState(false);

  // Phone Auth State
  const [needsPhone, setNeedsPhone] = useState(false);
  const [isPhoneLoginMode, setIsPhoneLoginMode] = useState(false);
  const [phoneInput, setPhoneInput] = useState("");
  const [otpInput, setOtpInput] = useState("");
  const [showOtpField, setShowOtpField] = useState(false);
  const [confirmationResult, setConfirmationResult] = useState(null);

  // Forgot Password State
  const [isForgotPasswordMode, setIsForgotPasswordMode] = useState(false);
  const [resetEmail, setResetEmail] = useState("");

  // --- Strict Google Verification State ---
  const [isAwaitingGoogleLink, setIsAwaitingGoogleLink] = useState(false);

  const clearRecaptcha = () => {
    if (window.recaptchaVerifier) {
      window.recaptchaVerifier.clear();
      window.recaptchaVerifier = null;
    }
  };

  useEffect(() => {
    return () => clearRecaptcha();
  }, []);

  useEffect(() => {
    if (
      (needsPhone || isPhoneLoginMode) &&
      !showOtpField &&
      !isAwaitingGoogleLink
    ) {
      setTimeout(() => {
        authService.setupRecaptcha("recaptcha-container").catch((err) => {
          console.error("Recaptcha Init Error:", err);
        });
      }, 100);
    }
  }, [needsPhone, isPhoneLoginMode, showOtpField, isAwaitingGoogleLink]);

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
  };

  // --- 1. SEND OTP ---
  const handleSendOtp = async (e) => {
    e.preventDefault();
    setGlobalError(null);
    setGlobalSuccess(null);

    if (!phoneInput || phoneInput.length !== 10) {
      setGlobalError("Please enter a valid 10-digit number.");
      return;
    }

    if (!window.recaptchaVerifier) {
      setGlobalError("Please wait for the security check to load.");
      return;
    }

    setLoading(true);

    try {
      const cleanPhone = phoneInput.replace(/^0+/, "");
      const formattedPhone = `+91${cleanPhone}`;
      let result;

      if (isPhoneLoginMode) {
        result = await authService.sendLoginOtp(formattedPhone);
      } else {
        result = await authService.linkPhoneOtp(formattedPhone);
      }

      setConfirmationResult(result);
      setShowOtpField(true);
    } catch (err) {
      console.error("OTP Send Error:", err);
      if (window.recaptchaVerifier) {
        try {
          const widgetId = await window.recaptchaVerifier.render();
          window.grecaptcha.reset(widgetId);
        } catch (e) {}
      }

      if (err.code === "auth/too-many-requests") {
        setGlobalError("Too many attempts. Try again later.");
      } else if (err.code === "auth/invalid-phone-number") {
        setGlobalError("Invalid phone number.");
      } else {
        setGlobalError("Failed to send OTP. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  // --- 2. VERIFY OTP ---
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setGlobalError(null);

    if (!confirmationResult) {
      setGlobalError("Please request OTP first.");
      return;
    }

    if (!otpInput || otpInput.length !== 6) {
      setGlobalError("Enter valid 6-digit OTP.");
      return;
    }

    setLoading(true);

    try {
      const result = await confirmationResult.confirm(otpInput);
      const cleanPhone = `+91${phoneInput.replace(/^0+/, "")}`;

      const backendData = await authService.syncUserWithBackend(result.user, {
        phone: cleanPhone,
      });

      if (!backendData.isProfileComplete) {
        // Trigger the mandatory Google Link screen overlay
        setIsAwaitingGoogleLink(true);
      } else {
        setGlobalSuccess("Successfully signed in!");
        window.location.href = "/";
      }
    } catch (err) {
      console.error(err);
      if (err.code === "auth/invalid-verification-code") {
        setGlobalError("Invalid OTP.");
      } else if (err.code === "auth/code-expired") {
        setGlobalError("OTP expired. Request again.");
      } else {
        setGlobalError("Verification failed. Try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  // --- 3. MANDATORY GOOGLE LINKING ---
  const handleMandatoryGoogleLink = async () => {
    setGlobalError(null);
    setLoading(true);

    try {
      const provider = new GoogleAuthProvider();
      // Link the current phone session with the Google Popup
      const result = await linkWithPopup(auth.currentUser, provider);

      const cleanPhone = `+91${phoneInput.replace(/^0+/, "")}`;

      // 🚨 Aggressively extract Name/Email from the hidden providerData array
      const googleData = result.user.providerData.find(
        (p) => p.providerId === "google.com",
      );
      const fullName = googleData?.displayName || result.user.displayName || "";
      const userEmail = googleData?.email || result.user.email || "";

      const fName = fullName.split(" ")[0] || "";
      const lName = fullName.split(" ").slice(1).join(" ") || "";

      // 🚨 Force update the top-level Firebase profile so your Navbar catches the name!
      if (fullName) {
        await updateProfile(result.user, { displayName: fullName });
      }

      // Sync the newly acquired Google details directly to Postgres
      await authService.syncUserWithBackend(result.user, {
        phone: cleanPhone,
        firstName: fName,
        lastName: lName,
        email: userEmail,
      });

      setGlobalSuccess("Verification complete!");
      window.location.href = "/";
    } catch (err) {
      console.error("Link Error:", err);
      if (err.code === "auth/credential-already-in-use") {
        setGlobalError(
          "This Google account is already registered. Please log out and sign in with Google directly.",
        );
      } else if (err.code === "auth/popup-closed-by-user") {
        setGlobalError("Verification popup was closed. Please try again.");
      } else {
        const backendErrorMessage =
          err.response?.data?.message ||
          err.response?.data?.error ||
          err.message ||
          "Unknown error occurred";
        setGlobalError(`Backend Error: ${backendErrorMessage}`);
      }
    } finally {
      setLoading(false);
    }
  };

  // Abort the process if the user refuses to link
  const handleCancelLinking = async () => {
    await signOut(auth);
    setIsAwaitingGoogleLink(false);
    setPhoneInput("");
    setOtpInput("");
    setShowOtpField(false);
    setIsPhoneLoginMode(false);
    setGlobalError(
      "Login aborted. Email verification is required to continue.",
    );
  };

  // --- 4. STANDARD EMAIL LOGIN ---
  const handleLogin = async (e) => {
    e.preventDefault();
    setGlobalError(null);
    setGlobalSuccess(null);

    if (!formData.email || !formData.password) {
      setGlobalError("Please fill in both email and password.");
      return;
    }

    try {
      setLoading(true);
      const backendData = await authService.login(
        formData.email,
        formData.password,
      );

      if (!backendData.isProfileComplete) {
        setIsAwaitingGoogleLink(true);
      } else {
        setGlobalSuccess("Successfully signed in!");
        window.location.href = "/";
      }
    } catch (err) {
      setGlobalError("Invalid email or password.");
    } finally {
      setLoading(false);
    }
  };

  // --- 5. GOOGLE LOGIN ---
  const handleGoogleLogin = async () => {
    setGlobalError(null);
    setGlobalSuccess(null);
    setLoading(true);
    try {
      const backendData = await authService.loginWithGoogle();

      if (
        !backendData.isProfileComplete &&
        backendData.missingFields.includes("phone_number")
      ) {
        setNeedsPhone(true);
      } else {
        window.location.href = "/";
      }
    } catch (err) {
      console.error(err);
      setGlobalError("Google Sign-In failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setGlobalError(null);
    setGlobalSuccess(null);

    if (!resetEmail) {
      setGlobalError("Please enter your email address.");
      return;
    }

    setLoading(true);
    try {
      await authService.resetPassword(resetEmail);
      setGlobalSuccess("Password reset email sent! Please check your inbox.");
      setResetEmail("");
    } catch (err) {
      if (err.code === "auth/user-not-found") {
        setGlobalError("No account found with this email address.");
      } else if (err.code === "auth/invalid-email") {
        setGlobalError("Please enter a valid email address.");
      } else {
        setGlobalError("Failed to send reset email. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAppleLogin = async () => {
    console.log("Apple login triggered");
  };

  return (
    <>
      {/* ========================================= */}
      {/* STRICT GOOGLE VERIFICATION OVERLAY        */}
      {/* ========================================= */}
      {isAwaitingGoogleLink && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.75)",
            zIndex: 99999,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backdropFilter: "blur(4px)",
          }}
        >
          <div
            className="auth-card"
            style={{
              width: "100%",
              maxWidth: "450px",
              margin: "20px",
              position: "relative",
              textAlign: "center",
              padding: "40px 30px",
            }}
          >
            <h2>Action Required</h2>
            <p
              style={{ color: "#ef4444", fontWeight: "bold", margin: "10px 0" }}
            >
              Email Verification Needed
            </p>
            <p
              style={{
                marginBottom: "25px",
                color: "#475569",
                lineHeight: "1.5",
              }}
            >
              To secure your account, you must link your Google account. This
              securely verifies your identity and imports your details.
            </p>

            {globalError && (
              <div className="message-banner error">{globalError}</div>
            )}

            <button
              className="google-btn"
              onClick={handleMandatoryGoogleLink}
              disabled={loading}
              style={{
                width: "100%",
                marginBottom: "20px",
                justifyContent: "center",
              }}
            >
              <GoogleIcon /> {loading ? "Verifying..." : "Verify with Google"}
            </button>

            <button
              onClick={handleCancelLinking}
              disabled={loading}
              style={{
                background: "none",
                border: "none",
                color: "#64748b",
                textDecoration: "underline",
                cursor: "pointer",
                width: "100%",
                padding: "10px",
              }}
            >
              Cancel and Log Out
            </button>
          </div>
        </div>
      )}

      {/* ========================================= */}
      {/* STANDARD AUTH CARD (Sits behind the overlay)*/}
      {/* ========================================= */}
      <div className="auth-card">
        {needsPhone || isPhoneLoginMode ? (
          /* BLOCK B: Phone Verification */
          <>
            <h2>
              {isPhoneLoginMode ? "Sign In with Phone" : "Verify Your Phone"}
            </h2>
            <p>
              {isPhoneLoginMode
                ? "Enter your phone number to receive an OTP."
                : "Please link a phone number to complete your account."}
            </p>

            {globalError && !isAwaitingGoogleLink && (
              <div className="message-banner error">{globalError}</div>
            )}

            {!showOtpField ? (
              <form onSubmit={handleSendOtp} className="auth-form">
                <div className="form-group">
                  <label>Phone Number</label>
                  <div style={{ display: "flex", gap: "10px" }}>
                    <span
                      style={{
                        padding: "10px",
                        background: "#f0f0f0",
                        borderRadius: "4px",
                      }}
                    >
                      +91
                    </span>
                    <input
                      type="tel"
                      value={phoneInput}
                      onChange={(e) =>
                        setPhoneInput(
                          e.target.value.replace(/\D/g, "").slice(0, 10),
                        )
                      }
                      placeholder="9876543210"
                      disabled={loading}
                      autoFocus
                    />
                  </div>
                </div>

                <div
                  id="recaptcha-container"
                  style={{
                    margin: "15px 0",
                    display: "flex",
                    justifyContent: "center",
                  }}
                ></div>

                <button type="submit" className="auth-btn" disabled={loading}>
                  {loading ? "Sending..." : "Send OTP"}
                </button>
              </form>
            ) : (
              <form onSubmit={handleVerifyOtp} className="auth-form">
                <div className="form-group">
                  <label>Enter 6-digit OTP</label>
                  <input
                    type="text"
                    value={otpInput}
                    onChange={(e) =>
                      setOtpInput(e.target.value.replace(/\D/g, "").slice(0, 6))
                    }
                    placeholder="123456"
                    disabled={loading}
                    autoFocus
                  />
                </div>
                <button type="submit" className="auth-btn" disabled={loading}>
                  {loading ? "Verifying..." : "Verify OTP"}
                </button>
                <button
                  type="button"
                  className="forgot-password"
                  onClick={() => {
                    setShowOtpField(false);
                    clearRecaptcha();
                  }}
                  style={{
                    background: "none",
                    border: "none",
                    width: "100%",
                    textAlign: "center",
                    marginTop: "15px",
                  }}
                >
                  Wrong number? Go back.
                </button>
              </form>
            )}

            {isPhoneLoginMode && !showOtpField && (
              <button
                onClick={() => {
                  setIsPhoneLoginMode(false);
                  setGlobalError(null);
                  clearRecaptcha();
                }}
                className="forgot-password"
                style={{
                  background: "none",
                  border: "none",
                  width: "100%",
                  textAlign: "center",
                  marginTop: "15px",
                }}
              >
                ← Back to Email Login
              </button>
            )}
          </>
        ) : isForgotPasswordMode ? (
          /* BLOCK C: Forgot Password */
          <>
            <div className="auth-card-header">
              <h2>Reset Password</h2>
              <p>Enter your email to receive a password reset link.</p>
              {globalError && !isAwaitingGoogleLink && (
                <div className="message-banner error">{globalError}</div>
              )}
              {globalSuccess && !isAwaitingGoogleLink && (
                <div
                  className="message-banner success"
                  style={{
                    color: "green",
                    backgroundColor: "#e6ffe6",
                    padding: "10px",
                    borderRadius: "4px",
                    marginBottom: "15px",
                  }}
                >
                  {globalSuccess}
                </div>
              )}
            </div>

            <form
              className="auth-form"
              onSubmit={handleResetPassword}
              noValidate
            >
              <div className="form-group">
                <label htmlFor="resetEmail">Email Address</label>
                <input
                  type="email"
                  id="resetEmail"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  placeholder="name@example.com"
                  disabled={loading}
                  autoFocus
                />
              </div>

              <button type="submit" className="auth-btn" disabled={loading}>
                {loading ? "Sending..." : "Send Reset Link"}
              </button>
            </form>

            <button
              onClick={() => {
                setIsForgotPasswordMode(false);
                setGlobalError(null);
                setGlobalSuccess(null);
              }}
              className="forgot-password"
              style={{
                background: "none",
                border: "none",
                width: "100%",
                textAlign: "center",
                marginTop: "15px",
              }}
              disabled={loading}
            >
              ← Back to Login
            </button>
          </>
        ) : (
          /* BLOCK D: Standard Email Login */
          <>
            <div className="auth-card-header">
              <h2>Welcome Back!</h2>
              <p>Sign in to continue</p>
              {globalError && !isAwaitingGoogleLink && (
                <div className="message-banner error">{globalError}</div>
              )}
              {globalSuccess && !isAwaitingGoogleLink && (
                <div
                  className="message-banner success"
                  style={{
                    color: "green",
                    backgroundColor: "#e6ffe6",
                    padding: "10px",
                    borderRadius: "4px",
                    marginBottom: "15px",
                  }}
                >
                  {globalSuccess}
                </div>
              )}
            </div>

            <form className="auth-form" onSubmit={handleLogin} noValidate>
              <div className="form-group">
                <label htmlFor="email">Email Address</label>
                <input
                  type="email"
                  id="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="name@example.com"
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="password">Password</label>
                <input
                  type="password"
                  id="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Enter your password"
                  disabled={loading}
                />
              </div>

              <button
                type="button"
                className="forgot-password"
                onClick={() => {
                  setIsForgotPasswordMode(true);
                  setGlobalError(null);
                  setGlobalSuccess(null);
                }}
                style={{
                  background: "none",
                  border: "none",
                  padding: 0,
                  textAlign: "right",
                  display: "block",
                  width: "100%",
                  cursor: "pointer",
                }}
              >
                Forgot Password?
              </button>

              <button type="submit" className="auth-btn" disabled={loading}>
                {loading ? "Signing In..." : "Sign In"}
              </button>
            </form>

            <div className="auth-divider">
              <span>OR</span>
            </div>

            <button
              className="google-btn"
              onClick={() => {
                setIsPhoneLoginMode(true);
                setGlobalError(null);
              }}
              type="button"
              disabled={loading}
              style={{ marginBottom: "10px" }}
            >
              <span style={{ fontSize: "1.2rem", marginRight: "5px" }}>📱</span>{" "}
              Sign In with Phone
            </button>

            <button
              className="google-btn"
              onClick={handleGoogleLogin}
              type="button"
              disabled={loading}
            >
              <GoogleIcon /> Sign In with Google
            </button>

            <button
              className="google-btn"
              onClick={handleAppleLogin}
              style={{ marginTop: "10px" }}
              type="button"
              disabled={loading}
            >
              <AppleIcon /> Sign In with Apple
            </button>

            <div className="auth-toggle">
              Don't have an account?
              <button onClick={onToggleView} disabled={loading}>
                Sign Up
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default LoginPage;
